import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { Box, Hidden, Typography } from "@material-ui/core";

import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import {
  fetchHistoricalDelays,
  fetchHistoricalSummary,
} from "../../../../services/insightApp/historicalAnalytics";

import DelayedOrdersTable, { DelayedOrder } from "./DelayedOrdersTable";
import HistoricalFilters from "./HistoricalFilters";
import HistoricalSummaryCards, {
  HistoricalSummary,
} from "./HistoricalSummaryCards";
import OrderTimelineModal from "./OrderTimelineModal";
import {
  defaultHistoricalFilters,
  HistoricalFilterValues,
  toQueryParams,
} from "./historicalUtils";

/**
 * INSIGHT-HIST-12 — the historical dashboard for one metric type.
 *
 * Preparation uses it first; Load (14) and Delivery (15) are the same screen
 * with a different metric type, so the controls and the numbers stay
 * consistent between them.
 *
 * The realtime dashboard is untouched: this is a separate screen.
 */

export interface HistoricalMetricPageProps {
  metricType: "PREPARATION" | "LOAD" | "DELIVERY" | "STATION";
  title: string;
  showStation?: boolean;
  showDriver?: boolean;
}

const HistoricalMetricPage: React.FunctionComponent<HistoricalMetricPageProps> = ({
  metricType,
  title,
  showStation = false,
  showDriver = false,
}) => {
  const match: any = useRouteMatch();
  const locationId = match?.params?.locationId;

  const [filters, setFilters] = useState<HistoricalFilterValues>(
    defaultHistoricalFilters(),
  );
  const [summary, setSummary] = useState<HistoricalSummary | null>(null);
  const [delays, setDelays] = useState<DelayedOrder[]>([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isDelaysLoading, setIsDelaysLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<DelayedOrder | null>(null);

  // Each load gets an id; a response from an older load is ignored, so a slow
  // earlier request can't overwrite a newer one.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!locationId) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    const params = { ...toQueryParams(filters), metricType };

    setIsSummaryLoading(true);
    setIsDelaysLoading(true);
    setSummaryError("");

    try {
      const res = await fetchHistoricalSummary(locationId, params);
      if (isStale()) return;
      setSummary(res?.data?.data || null);
    } catch (err) {
      if (isStale()) return;
      setSummary(null);
      // An error is kept separate from an empty result on purpose.
      setSummaryError("The historical summary could not be loaded.");
    } finally {
      if (!isStale()) {
        setIsSummaryLoading(false);
      }
    }

    try {
      const res = await fetchHistoricalDelays(locationId, {
        ...params,
        limit: 50,
      });
      if (isStale()) return;
      setDelays(res?.data?.data?.orders || []);
    } catch (err) {
      if (isStale()) return;
      setDelays([]);
      setError("The delayed orders could not be loaded.");
    } finally {
      if (!isStale()) {
        setIsDelaysLoading(false);
      }
    }
  }, [locationId, filters, metricType]);

  useEffect(() => {
    document.title = `Insight - ${title}`;
  }, [title]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>

      <Box style={{ marginTop: "18px" }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>

        <HistoricalFilters
          values={filters}
          onChange={setFilters}
          showStation={showStation}
          showDriver={showDriver}
          disabled={isSummaryLoading && isDelaysLoading}
        />

        <Box mt={2}>
          <HistoricalSummaryCards
            summary={summary}
            isLoading={isSummaryLoading}
            error={summaryError}
          />
        </Box>

        <Box mt={2}>
          <DelayedOrdersTable
            orders={delays}
            isLoading={isDelaysLoading}
            onSelectOrder={setSelectedOrder}
          />
        </Box>
      </Box>

      {selectedOrder && (
        <OrderTimelineModal
          open={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          locationId={locationId}
          orderNo={selectedOrder.orderNo}
          businessDate={selectedOrder.businessDate}
        />
      )}

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default HistoricalMetricPage;
